<template>
  <span ref="target" class="animated-number">
    <span v-if="prepend" v-html="prepend"></span>
    <span>{{ displayNumber }}</span>
    <span v-if="append" :class="appendClass" v-html="append"></span>
  </span>
</template>

<script lang="ts" setup>
import {ref, watch, onBeforeUnmount, useTemplateRef} from "vue";
import {useElementVisibility} from "@vueuse/core";
import {useDebugComponent} from "@/core/composable/useDebugComponent";

export type AnimatedNumberProps = {
  number: number;
  append?: string;
  prepend?: string;
  appendClass?: string;
  animate?: boolean;
  duration?: number;
};

const props = withDefaults(defineProps<AnimatedNumberProps>(), {
  number: 0,
  animate: true,
  duration: 300,
});

const displayNumber = ref<number>(props.number || 0);
const target = useTemplateRef<HTMLSpanElement>('target');
const targetIsVisible = useElementVisibility(target);

let rafId: number | null = null;
let startTime: number | null = null;
let startValue: number = 0;
let targetValue: number = 0;

// Easing функция для плавной анимации
const easeOutQuad = (t: number): number => t * (2 - t);

const animate = (timestamp: number) => {
  if (!startTime) startTime = timestamp;
  const elapsed = timestamp - startTime;
  const progress = Math.min(elapsed / props.duration, 1);
  
  const easedProgress = easeOutQuad(progress);
  const diff = targetValue - startValue;
  displayNumber.value = Math.round(startValue + diff * easedProgress);
  
  if (progress < 1) {
    rafId = requestAnimationFrame(animate);
  } else {
    displayNumber.value = targetValue;
    rafId = null;
    startTime = null;
  }
};

const startAnimation = (newValue: number) => {
  // Отменяем предыдущую анимацию
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
  }
  
  startValue = displayNumber.value;
  targetValue = newValue;
  startTime = null;
  
  rafId = requestAnimationFrame(animate);
};

const stopAnimation = () => {
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
    startTime = null;
  }
};

watch(() => props.number, (newValue) => {
  if (newValue === displayNumber.value) return;
  
  // Если элемент видим и анимация включена
  if (targetIsVisible.value && props.animate) {
    startAnimation(newValue);
  } else {
    // Мгновенное обновление если не видим или анимация выключена
    stopAnimation();
    displayNumber.value = newValue;
  }
});

// Очистка при размонтировании
onBeforeUnmount(() => {
  stopAnimation();
});

// Debug композабл для отслеживания ререндеров
useDebugComponent('AnimatedNumber', { collectRenders: true });
</script>

<style scoped lang="scss">
.animated-number {
  white-space: nowrap;
  display: inline-block;
  // GPU ускорение для плавного рендеринга
  transform: translateZ(0);
  will-change: auto;
}
</style>
